import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import MuiTooltip from '@mui/material/Tooltip';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import TuneIcon from '@mui/icons-material/Tune';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FlagIcon from '@mui/icons-material/Flag';

import { useFormatDateTime } from '../util/displayTimeZone';

import type { CognitiveScore, MarkerLabel } from '../api/cognitive';
import { rebinCognitiveTrace } from '../api/cognitive';
import { getSessionHiddenMetrics } from '../api/sessionMetrics';
import { setCaptureSessionGo } from '../api/sessions';
import type { SessionPipeline } from '../api/sessionPipeline';
import { MediaStrip, type MediaStripHandle } from './MediaStrip';
import { SessionPipelineStrip } from './SessionPipelineStrip';
import { MomentInspector } from './MomentInspector';
import {
  CoachingSessionView,
  TranscriptSourcePicker,
  useCoachingSessions,
} from './CoachingSessions';
import { RecordDebriefButton } from './debrief/RecordDebriefButton';
import { SessionSurveyCard } from './SessionSurveyCard';
import { TraceZoom, useTraceViewport } from './traceViewport';
import { PinnedMomentLabel } from './PinnedMomentLabel';
import { formatElapsedStamp, isSameMoment } from '../util/pinnedMoment';
import { CaptionTrack, type CaptionEntry } from './CaptionTrack';
import { silenceArtefacts, stripStrayScripts } from '../util/transcriptArtefacts';

/**
 * Cognitive trace for a single capture session — Focus + Emotional
 * Engagement varying through the recording, plotted as a continuous
 * time series.
 *
 * The insights-worker computes a sliding-window Welch PSD over each
 * phase's filtered EEG (4 s window, 1 s step) and stores the per-
 * window values in `cognitive_scores.trace_json`. This component
 * concatenates the per-phase traces in their natural order (baseline
 * first, then alphabetical) and offsets each phase's window centers
 * by the cumulative phase duration, so the x-axis runs continuously
 * from t=0 to t=session_duration.
 *
 * Fallback for older sessions:
 *   Rows scored before tenant cognitive_scores revision 16 carry
 *   `trace = null`. Those phases get a flat horizontal segment using
 *   the per-phase aggregate values — same as the pre-trace render —
 *   so historic captures still draw something instead of vanishing.
 *   Re-scoring through `insight_jobs` populates the trace.
 */

interface Props {
  /** The capture-app's `session_id` string (varchar 64). The
   *  cognitive_scores table keys on this, not the row UUID. */
  sessionId: string | null;
  /** CaptureSession PK UUID — what the session-attachments routes
   *  key on. Optional: when present (and `sessionId` is also set)
   *  the trace card fetches per-marker video frame previews and
   *  enables the hover/pin surface. ROADMAP — peak/valley video
   *  frames. */
  sessionRowId?: string | null;
  /** When set, the trace card hits the tenant-admin attachments
   *  route; when omitted, falls back to the participant `/me`
   *  surface. Same convention as the rest of the dashboard. */
  tenantId?: string | null;
  procedureName: string | null;
  capturedAt: string | null;
  /** Total session duration in seconds (session.ended_at -
   *  session.started_at). Used for the headline subtitle and as the
   *  fallback session-duration estimate when a phase has no trace. */
  durationS: number | null;
  /** Full cognitive feed the dashboard already fetched; this
   *  component filters by `session_id` client-side. */
  cognitiveScores: CognitiveScore[];
  /** COGNITIVE-TRACE-SMOOTHING: when true (tenant admin with write, and
   *  `tenantId` + `sessionId` set), render the smoothing slider that re-bins
   *  this session's trace. Omitted / false on participant self-views. */
  canRebin?: boolean;
  /** DEBRIEF-RECORDING: booth staff and up on the tenant surface — shows
   *  "Record debrief" (edge cameras + this laptop's microphone). */
  canRecordDebrief?: boolean;
  /** Whose session, for the recording bar. */
  participantLabel?: string | null;
  /** Called after a re-bin is requested so the parent re-fetches the
   *  cognitive scores; the card polls this until the new interval lands. */
  onRebinApplied?: () => void;
  /** Where the session's data is on its way in — shown under the title
   *  (SESSION-PIPELINE). Omitted where no status is loaded. */
  pipeline?: SessionPipeline | null;
  /** Rendered beside the chart (e.g. the 3D hand path), given the moment
   *  pinned on the trace. Its column lines up with the AI Coach column
   *  below the card; it collapses when the slot renders nothing. */
  chartAside?: (pinnedT: number | null) => ReactNode;
  /** ROLES-AND-GO: the role taken in this recording, shown in the header. */
  roleName?: string | null;
  /** Staff on the tenant surface: "Set Go here" at the pinned moment, which
   *  makes everything before it standby and re-scores the session. */
  canSetGo?: boolean;
  /** Called after Go was set, so the parent re-fetches the scores. */
  onGoSet?: () => void;
}

/** Aside width that puts the chart's right edge on the Session metrics card's
 *  right edge: AI Coach column (300) + gap (16) − this card's padding (16) −
 *  the chart/aside gap (16). */
const CHART_ASIDE_WIDTH_PX = 284;

type SeriesKey =
  | 'focus'
  | 'engagement'
  | 'workload'
  | 'stress'
  | 'fatigue'
  | 'valence';

interface TracePoint {
  /** Seconds elapsed from session start. */
  t: number;
  focus: number | null;
  engagement: number | null;
  workload: number | null;
  stress: number | null;
  fatigue: number | null;
  valence: number | null;
}

/** The six EEG indices the trace can draw, in chip order. Focus and
 *  Engagement are on by default; the viewer toggles the rest. Names are the
 *  line names (tooltip + tests key on them). Keys are also the Settings →
 *  Metrics keys: an index hidden there gets no chip and no line. */
const SERIES: {
  key: SeriesKey;
  name: string;
  color: string;
  /** Said under the chart when Settings shows this index but the recording
   *  has no values for it (no chip is offered then). */
  missing: string;
}[] = [
  { key: 'focus', name: 'Focus', color: '#1e3a8a', missing: 'No focus values in this recording.' },
  { key: 'engagement', name: 'Emotional Engagement', color: '#7c3aed', missing: 'No engagement values in this recording.' },
  { key: 'workload', name: 'Workload', color: '#0891b2', missing: 'No workload values in this recording.' },
  { key: 'stress', name: 'Stress', color: '#f59e0b', missing: 'Stress isn’t available for this recording: it was scored before Stress was added.' },
  { key: 'fatigue', name: 'Fatigue', color: '#db2777', missing: 'Fatigue isn’t available for this recording: it was scored before Fatigue was added.' },
  { key: 'valence', name: 'Valence', color: '#16a34a', missing: 'Valence isn’t available for this recording: it needs both front electrodes (AF7 + AF8).' },
];
const DEFAULT_SERIES: SeriesKey[] = ['focus', 'engagement'];
/** Per-viewer convenience: remember which series are on, in this browser. */
const SERIES_STORAGE_KEY = 'cognitiveTrace.series';

function loadShownSeries(): Set<SeriesKey> {
  try {
    const raw = window.localStorage.getItem(SERIES_STORAGE_KEY);
    if (raw) {
      const keys = (JSON.parse(raw) as string[]).filter((k): k is SeriesKey =>
        SERIES.some((s) => s.key === k),
      );
      if (keys.length > 0) return new Set(keys);
    }
  } catch {
    // Storage blocked / malformed — fall back to the default.
  }
  return new Set(DEFAULT_SERIES);
}

const BASELINE_PHASE = 'baseline';
const BASELINE_FALLBACK_S = 10;

/** Baseline first, then alphabetical. Stable across re-renders. */
function comparePhase(a: string, b: string): number {
  if (a === BASELINE_PHASE && b !== BASELINE_PHASE) return -1;
  if (b === BASELINE_PHASE && a !== BASELINE_PHASE) return 1;
  return a.localeCompare(b);
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(0)} s`;
  const min = Math.floor(seconds / 60);
  const sec = Math.round(seconds - min * 60);
  return sec === 0 ? `${min} min` : `${min} min ${sec} s`;
}

/** The standby phase of a two-stage start (ROLES-AND-GO): recorded before
 *  Go, left out of scores. Sorts before 'task', so it leads the trace. */
const STANDBY_PHASE = 'standby';

function buildSeries(
  scores: CognitiveScore[],
  sessionDurationS: number,
): {
  points: TracePoint[];
  anyTrace: boolean;
  /** Where the standby stretch sits on the trace, if the recording has one. */
  standby: { start: number; end: number } | null;
} {
  if (scores.length === 0) return { points: [], anyTrace: false, standby: null };
  const sorted = [...scores].sort((a, b) => comparePhase(a.phase, b.phase));
  let anyTrace = false;

  // Estimate per-phase durations for the flat-fallback path. Same
  // convention the prior aggregate-only renderer used: baseline gets
  // BASELINE_FALLBACK_S if it's present, remaining time split evenly
  // across the others. Only used for phases whose trace_json is null.
  const hasBaseline = sorted.some((s) => s.phase === BASELINE_PHASE);
  const baselineSlice = hasBaseline
    ? Math.min(BASELINE_FALLBACK_S, sessionDurationS)
    : 0;
  const nonBaselineCount = sorted.filter(
    (s) => s.phase !== BASELINE_PHASE,
  ).length;
  const nonBaselineSlice =
    nonBaselineCount > 0
      ? (sessionDurationS - baselineSlice) / nonBaselineCount
      : 0;

  const points: TracePoint[] = [];
  let cursor = 0;
  let standby: { start: number; end: number } | null = null;
  for (const s of sorted) {
    const phaseStart = cursor;
    if (s.trace && s.trace.focus.length > 0) {
      anyTrace = true;
      const { window_s, step_s, focus, engagement, workload, valence, stress, fatigue } =
        s.trace;
      for (let i = 0; i < focus.length; i++) {
        // Nominal window-centre time within the phase. Step + window
        // are emitted by the worker; the portal trusts them as the
        // canonical units rather than re-deriving.
        const tWithinPhase = i * step_s + window_s / 2;
        points.push({
          t: cursor + tWithinPhase,
          focus: focus[i],
          engagement: engagement[i],
          workload: workload?.[i] ?? null,
          stress: stress?.[i] ?? null,
          fatigue: fatigue?.[i] ?? null,
          valence: valence?.[i] ?? null,
        });
      }
      const phaseDuration =
        focus.length > 0 ? (focus.length - 1) * step_s + window_s : 0;
      cursor += phaseDuration;
    } else {
      // No per-window trace on this row — fall back to a flat
      // segment using the phase aggregate. The legacy-capture
      // warning in the headline tells the operator to re-score.
      const slice =
        s.phase === BASELINE_PHASE ? baselineSlice : nonBaselineSlice;
      if (slice > 0) {
        points.push({
          t: cursor,
          focus: s.focus,
          engagement: s.engagement,
          workload: s.workload,
          stress: null,
          fatigue: null,
          valence: s.valence,
        });
        points.push({
          t: cursor + slice,
          focus: s.focus,
          engagement: s.engagement,
          workload: s.workload,
          stress: null,
          fatigue: null,
          valence: s.valence,
        });
        cursor += slice;
      }
    }
    if (s.phase === STANDBY_PHASE && cursor > phaseStart) {
      standby = { start: phaseStart, end: cursor };
    }
  }
  return { points, anyTrace, standby };
}

/** Top-margin headroom (px). The chart no longer prints rotated marker
 *  headlines above the plot area (TRACE-NO-MARKER-BARS — the transcript track
 *  underneath tells that story now), so this is just enough to keep the top
 *  gridline off the card edge. */
const CHART_TOP_MARGIN_PX = 8;

// Marker headlines that are always boilerplate — never conversation context —
// so they're dropped from the trace (CAPTURE-TRACE-OMIT-SILENCE). Compared
// after normalising (trim, strip trailing dots, lowercase). New captures no
// longer emit these; filtering here also hides any already stored by older
// scoring, without a re-score.
const ALWAYS_OMIT_HEADLINES = new Set(['silence', 'session end']);

/** True when a marker label carries actual words. 'transcript' is the
 *  paraphrase-fallback headline: when the AI couldn't summarise it emits
 *  headline 'transcript' with the real speech in the body — those we keep. A
 *  'transcript' headline with no body is pure boilerplate and gets dropped. */
function markerHasBody(label: MarkerLabel): boolean {
  return label.summary.trim() !== '' || label.transcript.trim() !== '';
}

/**
 * Says that transcript lines were hidden as silence, rather than letting the
 * panel simply look short or empty. Without it, a recording whose room audio
 * was silent reads as "the transcript is broken", which is the opposite of
 * what happened: it was checked, and there was nothing to hear.
 */
function SilenceNote({ hidden, shown }: { hidden: number; shown: number }) {
  if (hidden === 0) return null;
  const lines = hidden === 1 ? 'line' : 'lines';
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      component="p"
      sx={{ mt: 1, fontStyle: 'italic' }}
    >
      {shown === 0
        ? `No speech detected in the room audio. ${hidden} transcript ${lines} hidden: the transcription service returned text for silence.`
        : `${hidden} transcript ${lines} hidden: no speech detected there, and the transcription service returned text for silence.`}
    </Typography>
  );
}

// Smoothing-slider stops: Off (native 1 s) then 5 s … 30 s. Only the ends
// carry a label so the rail stays uncluttered.
const SMOOTHING_MARKS = [
  { value: 0, label: 'Off' },
  { value: 5 },
  { value: 10 },
  { value: 15 },
  { value: 20 },
  { value: 25 },
  { value: 30, label: '30s' },
];

export function CognitiveTrace({
  sessionId,
  sessionRowId = null,
  tenantId = null,
  procedureName,
  capturedAt,
  durationS,
  cognitiveScores,
  canRebin = false,
  canRecordDebrief = false,
  participantLabel = null,
  onRebinApplied,
  pipeline = null,
  chartAside,
  roleName = null,
  canSetGo = false,
  onGoSet,
}: Props) {
  const theme = useTheme();
  const fmtDateTime = useFormatDateTime();
  // The moment pinned by clicking the trace (session-elapsed seconds), or null.
  // Drives the MediaStrip above: it seeks the recording's video to that instant
  // and plays a ±5 s audio window there. Replaces the old per-marker frame
  // previews / tagged stills / camera picker (the full clip + scrub supersedes
  // pre-extracted frames — MULTI-PARTICIPANT-FOCUS moment inspector).
  const [pinnedT, setPinnedT] = useState<number | null>(null);
  // Play all: whether the replay is running, and where its moving marker is
  // (session-elapsed seconds; kept after a pause so the next press resumes
  // there). The marker replaces the pin on the chart while it exists.
  const [playingAll, setPlayingAll] = useState(false);
  const [playheadT, setPlayheadT] = useState<number | null>(null);
  const markerT = playheadT ?? pinnedT;

  // ── COGNITIVE-TRACE-SMOOTHING ──────────────────────────────────────
  // The smoothing interval currently persisted for this session (seconds;
  // 0 = the native 1 s trace), read off the score rows the parent fetched.
  const sessionScores = cognitiveScores.filter(
    (s) => s.session_id === sessionId,
  );
  const sessionBin =
    sessionScores.find((s) => s.trace_bin_seconds != null)?.trace_bin_seconds ??
    0;
  // The step of the actually-loaded trace (native = 1, a bin of N = N). This
  // is what tells us the async re-bin *finished* — `trace_bin_seconds` flips
  // the instant the endpoint records intent, but `trace_json` (line + markers)
  // only changes once the retrace worker rewrites it.
  const loadedStep = Math.round(
    sessionScores.find((s) => s.trace)?.trace?.step_s ?? 1,
  );
  const showRebin = canRebin && !!tenantId && !!sessionId;
  const [sliderBin, setSliderBin] = useState(sessionBin);
  // The interval awaiting the admin's confirm (null = no dialog open).
  const [confirmBin, setConfirmBin] = useState<number | null>(null);
  // The interval we asked the server to build, while the async recompute
  // (markers → frames → labels) is in flight (null = idle).
  const [recomputingBin, setRecomputingBin] = useState<number | null>(null);
  const [rebinError, setRebinError] = useState<string | null>(null);
  const recomputeDeadline = useRef<number | null>(null);

  // Keep the slider synced to the persisted interval when the parent
  // refreshes — but never yank it out from under an open confirm dialog or
  // an in-flight recompute.
  useEffect(() => {
    if (confirmBin === null && recomputingBin === null) setSliderBin(sessionBin);
  }, [sessionBin, confirmBin, recomputingBin]);

  // Recompute lands: the refreshed trace's step now matches the interval we
  // asked for (native → step 1) → clear the "Recomputing…" state. Keyed on the
  // rebuilt trace, NOT on trace_bin_seconds, so we keep polling until the line
  // + markers actually change rather than stopping the moment intent is saved.
  useEffect(() => {
    if (recomputingBin !== null && loadedStep === (recomputingBin || 1)) {
      setRecomputingBin(null);
    }
  }, [loadedStep, recomputingBin]);

  // While recomputing, nudge the parent to re-fetch the scores every few
  // seconds so the flip is picked up; give up (stop the spinner) after a
  // bounded wait — the frames/labels may still be finishing server-side.
  useEffect(() => {
    if (recomputingBin === null) return;
    const id = window.setInterval(() => {
      if (recomputeDeadline.current && Date.now() > recomputeDeadline.current) {
        setRecomputingBin(null);
        return;
      }
      onRebinApplied?.();
    }, 2500);
    return () => window.clearInterval(id);
  }, [recomputingBin, onRebinApplied]);

  const applyRebin = useCallback(async () => {
    if (confirmBin === null || !tenantId || !sessionId) return;
    const target = confirmBin;
    setConfirmBin(null);
    setRebinError(null);
    try {
      await rebinCognitiveTrace(tenantId, sessionId, target || null);
      recomputeDeadline.current = Date.now() + 60_000;
      setRecomputingBin(target);
      onRebinApplied?.();
    } catch {
      setRebinError('Could not start the recompute. Please try again.');
      setSliderBin(sessionBin);
    }
  }, [confirmBin, tenantId, sessionId, onRebinApplied, sessionBin]);

  const cancelRebin = useCallback(() => {
    setConfirmBin(null);
    setSliderBin(sessionBin);
  }, [sessionBin]);

  const mediaRef = useRef<MediaStripHandle>(null);
  // Coaching sessions (COACHING-SESSIONS): debriefs recorded after the
  // procedure. When the session has any, a picker chooses whose transcript
  // (and player) the inspector shows; the procedure recording is the default.
  const { sessions: coachingSessions, angles: coachingAngles, reload: reloadCoaching } =
    useCoachingSessions(tenantId, sessionRowId);
  const [transcriptSource, setTranscriptSource] = useState('procedure');
  useEffect(() => setTranscriptSource('procedure'), [sessionRowId]);
  const coachingPick =
    coachingSessions.find((a) => a.id === transcriptSource) ?? null;

  /**
   * A click on the chart or a pick in the transcript. Landing on a new moment
   * pins it (the media below seeks + plays there). Landing on the moment
   * you're already on toggles that playback off — and on again — so the same
   * gesture that started the clip stops it, without hunting for the video's
   * own pause button. The pin itself stays put: you still want to see where
   * you are after stopping.
   */
  const pickMoment = useCallback(
    (t: number) => {
      if (isSameMoment(t, pinnedT)) {
        mediaRef.current?.togglePlayback();
        return;
      }
      setPinnedT(t);
      // Paused Play all resumes from the new pick rather than where it
      // stopped; while playing, the media moves its clock here itself.
      if (!playingAll) setPlayheadT(null);
    },
    [pinnedT, playingAll],
  );

  // Switching to another session clears the pinned moment so a moment pinned
  // on one recording's trace doesn't carry over to the next. The MediaStrip
  // above fetches its own video/audio for the new sessionRowId.
  useEffect(() => {
    setPinnedT(null);
    setPlayheadT(null);
  }, [sessionRowId, sessionId]);

  // Settings → Metrics visibility for this session (its procedure's
  // override, else the tenant default). Null until it arrives — and on an
  // error — which hides nothing.
  const [hiddenKeys, setHiddenKeys] = useState<Set<string> | null>(null);
  useEffect(() => {
    setHiddenKeys(null);
    if (sessionId === null) return;
    let live = true;
    getSessionHiddenMetrics(tenantId, sessionId)
      .then((keys) => live && setHiddenKeys(new Set(keys)))
      .catch(() => live && setHiddenKeys(null));
    return () => {
      live = false;
    };
  }, [tenantId, sessionId]);

  const { points, anyTrace, standby } = useMemo(() => {
    if (sessionId === null) return { points: [], anyTrace: false, standby: null };
    const own = cognitiveScores.filter((s) => s.session_id === sessionId);
    const effectiveDuration =
      durationS !== null && durationS > 0 ? durationS : Math.max(own.length, 1);
    return buildSeries(own, effectiveDuration);
  }, [sessionId, cognitiveScores, durationS]);

  const xMax = points.length > 0 ? points[points.length - 1].t : 0;

  // Which of the six series this recording actually has values for — a
  // pre-v1.1 trace has no Stress / Fatigue, a missing front electrode no
  // Valence. Those chips stay visible but disabled, saying why.
  const [shownSeries, setShownSeries] = useState<Set<SeriesKey>>(loadShownSeries);
  const availableSeries = useMemo(() => {
    const out = new Set<SeriesKey>();
    for (const p of points) {
      for (const { key } of SERIES) {
        const v = p[key];
        if (v !== null && Number.isFinite(v)) out.add(key);
      }
    }
    return out;
  }, [points]);
  const toggleSeries = useCallback((key: SeriesKey) => {
    setShownSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        window.localStorage.setItem(SERIES_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        // Storage blocked — the toggle still works for this view.
      }
      return next;
    });
  }, []);
  // The indices Settings → Metrics leaves visible for this session.
  const visibleSeries = SERIES.filter((s) => !hiddenKeys?.has(s.key));
  const chosen = visibleSeries.filter(
    (s) => shownSeries.has(s.key) && availableSeries.has(s.key),
  );
  // When everything this viewer had switched on is hidden by the settings
  // (say Focus + Engagement, the default pair), draw the first index that is
  // left rather than an empty chart.
  const drawnSeries =
    chosen.length > 0
      ? chosen
      : visibleSeries.filter((s) => availableSeries.has(s.key)).slice(0, 1);
  const drawnKeys = new Set(drawnSeries.map((s) => s.key));
  // Chips only for what can be drawn: shown in Settings AND present in this
  // recording. What Settings shows but the recording lacks is said once,
  // underneath, instead of a greyed-out chip.
  const chipSeries = visibleSeries.filter((s) => availableSeries.has(s.key));
  const absent = visibleSeries.filter((s) => !availableSeries.has(s.key));
  const absentNotes = (() => {
    const keys = new Set(absent.map((s) => s.key));
    const notes = absent
      .filter((s) => !(keys.has('stress') && keys.has('fatigue') && (s.key === 'stress' || s.key === 'fatigue')))
      .map((s) => s.missing);
    if (keys.has('stress') && keys.has('fatigue')) {
      notes.unshift(
        'Stress and Fatigue aren’t available for this recording: it was scored before they were added.',
      );
    }
    return notes;
  })();
  // Valence is a log asymmetry centred on 0 — let the axis go negative only
  // when it's drawn, so the ratio-only view keeps its 0 floor.
  const yDomain: [number | 'auto', 'auto'] = drawnSeries.some(
    (s) => s.key === 'valence',
  )
    ? ['auto', 'auto']
    : [0, 'auto'];

  // How much of the trace one screen-width shows (TRACE-VIEWPORT). Drawing a
  // whole session across the card put ~16 points on every pixel for a long
  // case; the plot is now a fixed seconds-per-pixel and the rest scrolls.
  const viewport = useTraceViewport(xMax);
  const { scrollToTime, followTime } = viewport;
  useEffect(() => {
    // A transcript pick can land off-screen once the trace scrolls — follow
    // it, or the marker it just set is invisible.
    if (pinnedT != null) scrollToTime(pinnedT);
  }, [pinnedT, scrollToTime]);
  useEffect(() => {
    // Play all: keep the moving marker in view.
    if (playingAll && playheadT != null) followTime(playheadT);
  }, [playingAll, playheadT, followTime]);

  const hasMedia = !!sessionRowId && !coachingPick;

  // ── Set Go afterwards (ROLES-AND-GO) ──────────────────────────────
  const showSetGo = canSetGo && !!tenantId && !!sessionRowId;
  const [goConfirm, setGoConfirm] = useState<number | null>(null);
  const [goBusy, setGoBusy] = useState(false);
  const [goNotice, setGoNotice] = useState<string | null>(null);
  const applyGo = useCallback(async () => {
    if (goConfirm === null || !tenantId || !sessionRowId) return;
    setGoBusy(true);
    try {
      await setCaptureSessionGo(tenantId, sessionRowId, goConfirm);
      setGoNotice(
        goConfirm === 0
          ? 'Go cleared: the whole recording counts. Re-scoring in the background.'
          : `Go set at ${formatElapsedStamp(goConfirm)}. Re-scoring in the background; the trace updates when it's done.`,
      );
      setGoConfirm(null);
      onGoSet?.();
    } catch {
      setGoNotice('Could not set Go. Please try again.');
      setGoConfirm(null);
    } finally {
      setGoBusy(false);
    }
  }, [goConfirm, tenantId, sessionRowId, onGoSet]);
  const togglePlayAll = useCallback(() => {
    const media = mediaRef.current;
    if (!media) return;
    if (playingAll) {
      media.pauseAll();
      return;
    }
    let from = playheadT ?? pinnedT ?? 0;
    // At the end already → start over.
    if (from >= xMax - 0.5) from = 0;
    media.playAll(from);
  }, [playingAll, playheadT, pinnedT, xMax]);

  // Per-marker audio paraphrase labels (ROADMAP TRACE-MARKER-LABELS).
  // Each cognitive_scores row carries its phase's labels keyed by
  // session-elapsed `time_s` to 2-decimal precision. Accumulate
  // across rows into a single lookup; renders below as both the
  // always-visible rotated headline above each marker and the
  // "Conversation context" section inside the hover preview.
  // Labels judged to be silence artefacts — text Whisper invented over an
  // empty room. Counted so the panel can say lines were hidden instead of
  // just looking short (see util/transcriptArtefacts).
  const { labelMap, hiddenAsSilence } = useMemo(() => {
    const m = new Map<string, MarkerLabel>();
    if (sessionId === null) return { labelMap: m, hiddenAsSilence: 0 };
    // Gather the whole session first: the loop test is session-wide, and a
    // session's labels are split across one score row per phase.
    const all: Array<[string, MarkerLabel]> = [];
    for (const score of cognitiveScores) {
      if (score.session_id !== sessionId) continue;
      if (!score.marker_labels) continue;
      all.push(...Object.entries(score.marker_labels));
    }
    const verdicts = silenceArtefacts(
      all.map(([, label]) => ({
        text: label.transcript,
        noSpeech: label.confidence?.no_speech_prob,
      })),
    );
    let hidden = 0;
    all.forEach(([t, label], i) => {
      if (verdicts[i] !== null) {
        hidden += 1;
        return;
      }
        // Drop boilerplate headlines — noise, not conversation context
        // (CAPTURE-TRACE-OMIT-SILENCE). 'silence' / 'session end' always;
        // 'transcript' only when it has no body (keep the paraphrase-fallback
        // ones that carry the real spoken words).
        const normalized = label.headline
          .trim()
          .replace(/\.+$/, '')
          .toLowerCase();
        if (ALWAYS_OMIT_HEADLINES.has(normalized)) {
          return;
        }
        if (normalized === 'transcript' && !markerHasBody(label)) {
          return;
        }
        m.set(t, label);
    });
    return { labelMap: m, hiddenAsSilence: hidden };
  }, [sessionId, cognitiveScores]);

  // The transcript caption track: one row per labelled moment, keyed to its
  // session-elapsed second. Click a row → pin that instant (video seeks +
  // audio window). Same data that drives the headlines above each marker.
  const captionEntries = useMemo<CaptionEntry[]>(() => {
    const rows: CaptionEntry[] = [];
    for (const [t, label] of labelMap.entries()) {
      if (label.segments && label.segments.length > 0) {
        // Dense captions: one row per Whisper segment; the AI headline rides
        // on the first line of the window.
        label.segments.forEach((seg, i) => {
          // Labels stored before the worker stripped Whisper's stray
          // wrong-script letters still carry them.
          const text = stripStrayScripts(seg.text.trim());
          if (!text) return;
          rows.push({
            t: seg.start_s,
            headline: i === 0 ? label.headline : '',
            transcript: text,
            summary: i === 0 ? label.summary : '',
          });
        });
      } else {
        rows.push({
          t: Number(t),
          headline: label.headline,
          transcript: stripStrayScripts(label.transcript),
          summary: label.summary,
        });
      }
    }
    return rows;
  }, [labelMap]);

  return (
    <>
    <Card>
      <CardContent>
        <Stack spacing={2}>
          {/* Where this session's data is on its way in (SESSION-PIPELINE)
              — right under the title, since "why is this empty?" is the
              first question a fresh session raises. */}
          {pipeline && <SessionPipelineStrip pipeline={pipeline} />}

          {/* Moment inspector — the recording above the trace, with the
              transcript beside it on a wide screen rather than under it: a
              16:9 clip capped at 300px tall inside a full-width card painted
              ~1250px of black either side of itself, and the transcript is
              what you read while watching. Same block, same gestures, as the
              Analysis workspace. */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TranscriptSourcePicker
              sessions={coachingSessions}
              value={coachingPick ? coachingPick.id : 'procedure'}
              onChange={setTranscriptSource}
            />
            {canRecordDebrief && tenantId && sessionRowId && (
              <RecordDebriefButton
                tenantId={tenantId}
                sessionRowId={sessionRowId}
                label={participantLabel ?? procedureName ?? 'this session'}
                onRecorded={reloadCoaching}
              />
            )}
          </Stack>
          {coachingPick ? (
            <CoachingSessionView
              key={coachingPick.id}
              session={coachingPick}
              angles={coachingAngles[coachingPick.id] ?? []}
              tenantId={tenantId}
              onChanged={reloadCoaching}
            />
          ) : sessionRowId ? (
            <MomentInspector
              media={
                <MediaStrip
                  ref={mediaRef}
                  tenantId={tenantId}
                  sessionRowId={sessionRowId}
                  participantName={procedureName ?? 'Recording'}
                  selectedTime={pinnedT}
                  startedAt={capturedAt ?? new Date().toISOString()}
                  endT={xMax > 0 ? xMax : undefined}
                  onPlayheadTime={setPlayheadT}
                  onPlayAllChange={setPlayingAll}
                />
              }
              aside={
                <>
                  {/* What the participant said about the case themselves,
                      beside what the sensors said about it
                      (CAPTURE-SESSION-SURVEY). */}
                  <SessionSurveyCard
                    tenantId={tenantId}
                    sessionId={sessionId}
                  />
                  {captionEntries.length > 0 && (
                    <CaptionTrack
                      entries={captionEntries}
                      pinnedT={pinnedT}
                      onPick={pickMoment}
                      fill
                    />
                  )}
                  <SilenceNote hidden={hiddenAsSilence} shown={captionEntries.length} />
                </>
              }
            />
          ) : (
            <>
              <CaptionTrack
                entries={captionEntries}
                pinnedT={pinnedT}
                onPick={pickMoment}
              />
              <SilenceNote hidden={hiddenAsSilence} shown={captionEntries.length} />
            </>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 2,
              alignItems: 'flex-start',
            }}
          >
          <Box sx={{ flex: '1 1 0', width: '100%', minWidth: 0 }}>
          {/* Title + smoothing sit directly above the chart they describe,
              below the recording — the card reads media first, then the
              trace — and level the chart column with the aside beside it. */}
          <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {/* Same recoloured EEG-head glyph the metric scorecard's
                Cognitive (EEG) section uses (CSS-masked PNG → primary.main in
                both themes), at the scorecard's larger size — so the trace
                card and the EEG scorecard read as one family. */}
            <Box
              aria-hidden
              sx={{
                width: 42,
                height: 42,
                flexShrink: 0,
                bgcolor: 'primary.main',
                WebkitMaskImage: 'url(/metric-eeg.png)',
                maskImage: 'url(/metric-eeg.png)',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: 'center',
                maskPosition: 'center',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Cognitive trace
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {procedureName ?? 'Session'}
                {roleName ? ` · ${roleName}` : ''}
                {capturedAt ? ` · ${fmtDateTime(capturedAt)}` : ''}
                {durationS !== null
                  ? ` · ${formatDuration(durationS)} elapsed`
                  : ''}
                {!anyTrace && points.length > 0 ? (
                  <>
                    {' · '}
                    <span style={{ color: '#f59e0b' }}>
                      legacy capture (re-score for per-window detail)
                    </span>
                  </>
                ) : null}
              </Typography>
            </Box>
            {hasMedia && points.length > 0 && (
              <MuiTooltip
                describeChild
                title={
                  playingAll
                    ? 'Pause the replay'
                    : 'Replay the trace with every video and the audio, from the marker (or the start)'
                }
              >
                <Button
                  size="small"
                  variant={playingAll ? 'contained' : 'outlined'}
                  startIcon={playingAll ? <PauseIcon /> : <PlayArrowIcon />}
                  onClick={togglePlayAll}
                  aria-pressed={playingAll}
                  sx={{ flexShrink: 0 }}
                >
                  {playingAll ? 'Pause' : 'Play all'}
                </Button>
              </MuiTooltip>
            )}
            {showSetGo && (pinnedT != null || standby) && (
              <MuiTooltip
                describeChild
                title={
                  pinnedT != null
                    ? 'Everything before this moment becomes standby: left out of the scores and the duration'
                    : 'Clear the standby: the whole recording counts'
                }
              >
                <Button
                  size="small"
                  variant="text"
                  startIcon={<FlagIcon />}
                  onClick={() => setGoConfirm(pinnedT ?? 0)}
                  sx={{ flexShrink: 0 }}
                >
                  {pinnedT != null ? 'Set Go here' : 'Clear standby'}
                </Button>
              </MuiTooltip>
            )}
            {showRebin && (
              <Box sx={{ minWidth: 208, px: 1 }}>
                {/* Label + current value / status sit ABOVE the slider so the
                    status never collides with the Off / 30s mark labels the
                    slider renders beneath its rail. */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ minHeight: 22 }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TuneIcon
                      sx={{ fontSize: 15, color: 'text.secondary' }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 700, color: 'text.secondary' }}
                    >
                      Smoothing
                    </Typography>
                  </Stack>
                  {recomputingBin !== null ? (
                    <Chip
                      size="small"
                      color="primary"
                      variant="filled"
                      icon={
                        <CircularProgress
                          size={11}
                          thickness={6}
                          sx={{ color: 'inherit' }}
                        />
                      }
                      label="Recomputing…"
                      sx={{
                        height: 20,
                        fontWeight: 700,
                        '& .MuiChip-icon': { ml: 0.75, color: 'inherit' },
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  ) : (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={sessionBin === 0 ? 'default' : 'primary'}
                      label={sessionBin === 0 ? 'Off' : `${sessionBin}s`}
                      sx={{
                        height: 20,
                        fontWeight: 700,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}
                </Stack>
                <MuiTooltip title="Smooth the trace: median over N-second intervals">
                  <Slider
                    aria-label="Trace smoothing (seconds)"
                    value={sliderBin}
                    min={0}
                    max={30}
                    step={5}
                    size="small"
                    marks={SMOOTHING_MARKS}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => (v === 0 ? 'Off' : `${v}s`)}
                    disabled={recomputingBin !== null}
                    onChange={(_, v) => setSliderBin(v as number)}
                    onChangeCommitted={(_, v) => {
                      const nv = v as number;
                      if (nv !== sessionBin) setConfirmBin(nv);
                    }}
                    sx={{ mt: 0.25 }}
                  />
                </MuiTooltip>
              </Box>
            )}
          </Stack>
          </Box>
          {points.length === 0 ? (
            <Alert severity="info" variant="outlined">
              No cognitive trace yet for this recording. The
              insights-worker scores cognitive metrics shortly after a
              capture-session uploads its NDJSON to S3 — refresh once
              the worker has caught up.
            </Alert>
          ) : (
            <Box
              sx={{
                // One height in every state now that nothing is printed
                // above the plot area.
                height: 280,
                position: 'relative',
                // Chart on top, legend row beneath — the legend is a
                // plain DOM row (not the recharts <Legend>), so it can
                // never collide with the "Seconds elapsed" axis label.
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <TraceZoom
                spanS={viewport.spanS}
                onChange={viewport.setSpanS}
                durationS={xMax}
              />
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
              {/* The Y axis is drawn once, outside the scroller, so values
                  stay readable when the plot is panned. The scrolling chart
                  hides its own axis but keeps the same margins, so the two
                  plot areas line up. */}
              {viewport.scrolls && (
                <Box sx={{ width: 48, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={points}
                      margin={{
                        top: CHART_TOP_MARGIN_PX,
                        right: 0,
                        bottom: 28,
                        left: 0,
                      }}
                    >
                      <YAxis
                        tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                        stroke={theme.palette.text.secondary}
                        domain={yDomain}
                        width={48}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
              <Box
                ref={viewport.ref}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  overflowX: viewport.scrolls ? 'auto' : 'hidden',
                  overflowY: 'hidden',
                }}
              >
              <ResponsiveContainer
                width={viewport.chartWidth || '100%'}
                height="100%"
              >
                <LineChart
                  data={points}
                  margin={{
                    top: CHART_TOP_MARGIN_PX,
                    right: 16,
                    // Just enough room for the tick row + the "Seconds
                    // elapsed" axis label — the legend lives outside the
                    // SVG now, so nothing competes for this band.
                    bottom: 28,
                    left: 0,
                  }}
                  onClick={(state: { activeLabel?: number | string }) => {
                    // Pin the clicked instant → the MediaStrip above seeks the
                    // recording's video there and plays a ±5 s audio window.
                    const x =
                      typeof state?.activeLabel === 'number'
                        ? state.activeLabel
                        : Number(state?.activeLabel);
                    if (Number.isFinite(x)) pickMoment(x);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid
                    stroke={theme.palette.divider}
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    type="number"
                    dataKey="t"
                    domain={[0, xMax]}
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    stroke={theme.palette.text.secondary}
                    tickFormatter={(v: number) => `${v.toFixed(0)}s`}
                    label={{
                      value: 'Seconds elapsed',
                      position: 'insideBottom',
                      offset: -8,
                      style: {
                        fontSize: 11,
                        fill: theme.palette.text.secondary,
                      },
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                    stroke={theme.palette.text.secondary}
                    domain={yDomain}
                    width={48}
                    hide={viewport.scrolls}
                  />
                  <Tooltip
                    labelFormatter={(label) =>
                      typeof label === 'number'
                        ? `t = ${label.toFixed(0)}s`
                        : String(label)
                    }
                    formatter={(value, name) => {
                      const n = typeof value === 'number' ? value : null;
                      return [
                        n === null ? '—' : n.toFixed(2),
                        String(name),
                      ];
                    }}
                  />
                  {standby && (
                    // Two-stage start: recorded before Go, left out of the
                    // scores and the duration (ROLES-AND-GO).
                    <ReferenceArea
                      x1={standby.start}
                      x2={standby.end}
                      fill={theme.palette.text.disabled}
                      fillOpacity={0.12}
                      ifOverflow="hidden"
                      label={{
                        value: 'Standby',
                        position: 'insideTopLeft',
                        fontSize: 11,
                        fill: theme.palette.text.secondary,
                      }}
                    />
                  )}
                  {drawnSeries.map((ser) => (
                    <Line
                      key={ser.key}
                      type="monotone"
                      dataKey={ser.key}
                      name={ser.name}
                      stroke={ser.color}
                      strokeWidth={2.75}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
                  {/* The only vertical line on this chart is the moment
                      you're looking at — set by a click here or by picking a
                      transcript line below, and replaced by the next one.
                      The per-peak/valley bars are gone (TRACE-NO-MARKER-BARS):
                      on an 80-minute recording they numbered in the dozens,
                      which is past what anyone can read, and the transcript
                      track underneath now carries the "what happened here"
                      story they were standing in for. Same call the Analysis
                      overlay already made. */}
                  {markerT != null && (
                    <ReferenceLine
                      x={markerT}
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      label={
                        <PinnedMomentLabel
                          text={formatElapsedStamp(markerT)}
                          color={theme.palette.primary.main}
                          halo={theme.palette.background.paper}
                        />
                      }
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
              </Box>
              </Box>
              {/* Series toggles double as the legend — a plain DOM row below
                  the x-axis label, clear of the "Seconds elapsed" caption.
                  Click a chip to draw / hide that EEG index. */}
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
                justifyContent="center"
                sx={{ pt: 0.5, flexShrink: 0 }}
                role="group"
                aria-label="EEG series"
              >
                {chipSeries.map((ser) => {
                  const on = drawnKeys.has(ser.key);
                  return (
                    <Box key={ser.key} component="span">
                    <Chip
                      size="small"
                      label={ser.name}
                      clickable
                      onClick={() => toggleSeries(ser.key)}
                      aria-pressed={on}
                      variant={on ? 'filled' : 'outlined'}
                      icon={
                        <Box
                          component="span"
                          sx={{
                            width: 12,
                            height: 3,
                            borderRadius: 1,
                            bgcolor: ser.color,
                            opacity: on ? 1 : 0.5,
                          }}
                        />
                      }
                      sx={{
                        fontWeight: on ? 700 : 500,
                        '& .MuiChip-icon': { ml: 1 },
                      }}
                    />
                    </Box>
                  );
                })}
              </Stack>
              {absentNotes.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="p"
                  sx={{ textAlign: 'center', mt: 0.5, flexShrink: 0 }}
                >
                  {absentNotes.join(' ')}
                </Typography>
              )}
            </Box>
          )}
          </Box>
          {chartAside && (
            <Box
              sx={{
                flex: { lg: `0 0 ${CHART_ASIDE_WIDTH_PX}px` },
                width: '100%',
                minWidth: 0,
                '&:empty': { display: 'none' },
              }}
            >
              {chartAside(pinnedT)}
            </Box>
          )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
      {goNotice && (
        <Alert severity="info" sx={{ mt: 1 }} onClose={() => setGoNotice(null)}>
          {goNotice}
        </Alert>
      )}
      <Dialog open={goConfirm !== null} onClose={() => setGoConfirm(null)}>
        <DialogTitle>
          {goConfirm === 0 ? 'Clear the standby?' : `Set Go at ${formatElapsedStamp(goConfirm ?? 0)}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {goConfirm === 0
              ? 'The whole recording counts again in the scores and the duration.'
              : `The first ${formatElapsedStamp(goConfirm ?? 0)} of the recording becomes standby: it stays in the recording and the video, but is left out of the scores, the duration and the reports.`}{' '}
            The session is re-scored and its transcript made again, in the background.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoConfirm(null)}>Cancel</Button>
          <Button variant="contained" disabled={goBusy} onClick={() => void applyGo()}>
            {goConfirm === 0 ? 'Clear' : 'Set Go'}
          </Button>
        </DialogActions>
      </Dialog>
      {rebinError && (
        <Alert
          severity="error"
          sx={{ mt: 1 }}
          onClose={() => setRebinError(null)}
        >
          {rebinError}
        </Alert>
      )}
      <Dialog open={confirmBin !== null} onClose={cancelRebin}>
        <DialogTitle>
          {confirmBin === 0
            ? 'Reset the cognitive timeline?'
            : 'Rebuild the cognitive timeline?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmBin === 0
              ? 'Restore this session’s trace to its native 1-second detail.'
              : `Smooth this session’s trace into ${confirmBin}-second intervals (the median value within each interval).`}{' '}
            This recomputes the peak/valley markers and rebuilds the audio
            labels for this session. It runs in the background and updates
            here when it’s done.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRebin}>Cancel</Button>
          <Button variant="contained" onClick={applyRebin}>
            {confirmBin === 0 ? 'Reset' : 'Rebuild'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
